<script setup lang="ts">
import { useFlowControlStore } from '@components/flow-controller'
import { IconExclamationCircle } from '@tabler/icons-vue'
import { computed, ref, watch } from 'vue'

import BaseCard from '@tix/ds/components/atoms/BaseCard.vue'
import FormInput from '@tix/ds/components/atoms/FormInput.vue'
import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'

import { getErrorForAction } from '@/helpers/flowActionErrors'

import { useBundleStepTracking } from '@/composables/useBundleStepTracking'

import ActionHeader from './ActionHeader.vue'
import { getMappedFieldsFromSpec } from './surveyFieldMapping'

const store = useFlowControlStore()
const { trackBundleStep } = useBundleStepTracking()
const actionType = 'survey' as const

const action = computed(() => (store.action?.type === actionType && store.action) || null)

const spec = computed(() => {
  return action.value?.options?.additional_info_spec
})
const requiredKeys = computed(() => {
  // API specs doesn't recognise this as an array for some reason
  return new Set(spec.value?.required as Array<string>)
})

const formFields = computed(() => {
  if (!spec.value) return []
  const surveyIdPrefix = action.value ? `survey-${action.value.resolver_id}-` : ''
  return getMappedFieldsFromSpec(spec.value, surveyIdPrefix, requiredKeys.value)
})

const prevInput = store.currentActionPreviousInput
// Prefill form when user navigates back to the survey step (previous answers from flow store)
const initialSurveyAnswers: Record<string, any> =
  prevInput?.type === 'survey' ? { ...prevInput.input.additional_info } : {}

const responses = ref<Record<string, any>>({})

// Dedup blur/change and skip untouched fields; keyed by content because bundle_step_value is the field key.
let lastTrackedValues: Record<string, any> = {}

watch(
  formFields,
  (fields) => {
    const next: Record<string, any> = {}
    for (const { key } of fields) {
      next[key] = responses.value[key] ?? initialSurveyAnswers[key] ?? ''
    }
    responses.value = next
    lastTrackedValues = { ...next } // prefilled back-nav answers aren't fresh edits
  },
  { immediate: true },
)

function trackFieldChange(key: string, value: unknown): void {
  if (lastTrackedValues[key] === value) return

  lastTrackedValues[key] = value
  trackBundleStep(key)
}

function handleFieldInteraction(key: string): void {
  trackFieldChange(key, responses.value[key])
}

/** Select fields use Multiselect — track on model update, not blur/change. */
function handleFieldModelUpdate(key: string, type: unknown, value: unknown): void {
  if (type !== 'select') return
  trackFieldChange(key, value)
}

const title = computed(() => action.value?.meta?.summary_title)
const prompt = computed(() => action.value?.meta?.prompt)

const error = ref<{ type: 'client-side' | 'server-side'; message?: string } | null>(null)

const errorMessage = computed<string | null>(() => {
  if (!action.value) return `Action is not a ${actionType} action`
  if (error.value) return error.value.message ?? 'Validation error'
  return null
})

// Called by flow controller on submit: validate required fields, then return payload for API
store.registerInputGetter(() => {
  error.value = null
  const allRequiredFilled = [...requiredKeys.value].every((key) => (responses.value[key] ?? '') !== '')
  if (!allRequiredFilled) {
    error.value = { type: 'client-side', message: 'Please complete all required fields.' }
    return { isValid: false }
  }
  const additionalInfo = Object.fromEntries(formFields.value.map(({ key }) => [key, responses.value[key]]))
  return { isValid: true, input: { additional_info: additionalInfo } }
})

store.registerErrorHandler((apiError: unknown) => {
  const { message } = getErrorForAction(apiError, actionType)
  if (message) error.value = { type: 'server-side', message }
  return Boolean(message)
})

watch(responses, () => (error.value = null), { deep: true })
</script>

<template>
  <section class="survey-action">
    <template v-if="action">
      <ActionHeader :description="prompt" headingTag="h2" :title="title" />

      <BaseCard v-if="formFields.length" padding="comfy">
        <TransitionGroup appear class="survey-fields" name="transition-group-item" tag="div">
          <FormInput
            v-for="({ key, props }, index) in formFields"
            :key="key"
            v-bind="props"
            v-model="responses[key]"
            :style="{ '--index': index }"
            @blur="handleFieldInteraction(key)"
            @change="handleFieldInteraction(key)"
            @update:modelValue="handleFieldModelUpdate(key, props.type, $event)"
          />
        </TransitionGroup>
      </BaseCard>
    </template>

    <StatusMessage :icon="IconExclamationCircle" :message="errorMessage" variant="error" />
  </section>
</template>

<style scoped>
.survey-action {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.survey-fields {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5);
}

.transition-group-item-enter-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: calc(180ms * (var(--index, 0) / (var(--index, 0) + 2)));
}
.transition-group-item-enter-from {
  opacity: 0;
  transform: translateY(30px);
}
</style>
